import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, Quote, Mail, MessageCircle, CheckCircle2, Plus, Minus, Zap,
} from "lucide-react";
import { useState } from "react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Counter from "../components/Counter";
import {
  COMMODITIES, INDUSTRIES, SERVICES, WHY, WHY_POINTS, PROCESS, TESTIMONIALS,
  CLIENTS, FAQ, CONTACT,
} from "../data/site";

const wa = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent("Hi SourceX Global, I'd like to discuss a sourcing requirement.")}`;

export default function Home() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen bg-navy text-white overflow-hidden flex items-center">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy to-navy-soft" />
        <div className="absolute -right-32 top-1/4 h-96 w-96 rounded-full bg-brand-orange/20 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-brand-orange/10 blur-3xl" />
        <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />

        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 pt-28 pb-16 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="overline mb-6">
            Global Sourcing & Procurement — India
          </motion.p>

          <h1 className="font-display font-black font-fluid-hero tracking-tighter max-w-5xl">
            <MaskedLines lines={["Your Trusted India", "Sourcing Partner"]} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-8 text-white/70 max-w-2xl text-lg leading-relaxed"
          >
            Connect with verified Indian manufacturers for high-quality engineering products at
            competitive prices — across 45+ commodities and 18 industries.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link to="/quote" data-testid="hero-quote-btn" className="inline-flex items-center gap-2 bg-brand-orange text-white px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors group">
              Request a Quote <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              Contact Us
            </Link>
            <Link to="/supplier-registration" className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              Register as Supplier
            </Link>
            <Link to="/buyer-registration" className="inline-flex items-center gap-2 glass-dark px-7 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              Register as Buyer
            </Link>
            <a href={`mailto:${CONTACT.email}`} className="inline-flex items-center gap-2 glass-dark px-5 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white/15 transition-colors">
              <Mail size={15} /> Email Us
            </a>
            <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:opacity-90 transition-opacity">
              <MessageCircle size={15} /> WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-8"
          >
            {["USA", "Europe", "UK", "Middle East", "Australia"].map((r) => (
              <span key={r} className="font-mono uppercase text-[11px] tracking-[0.2em] text-white/50">{r}</span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CLIENT MARQUEE */}
      <section className="bg-secondary border-y border-border py-8 overflow-hidden">
        <Marquee speed={40} gradient={false}>
          {[...CLIENTS, ...CLIENTS].map((c, i) => (
            <span key={i} className="mx-10 font-display font-black text-2xl md:text-3xl text-muted-foreground/50 uppercase tracking-wider">{c}</span>
          ))}
        </Marquee>
      </section>

      {/* FASTTRACK BANNER */}
      <section className="bg-navy py-16 md:py-20 border-y-2 border-red-600/40">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden bg-gradient-to-r from-red-700 to-red-600 p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 text-white font-mono uppercase text-[11px] tracking-[0.2em] mb-4 fasttrack-blink">
                <Zap size={15} fill="white" /> SourceX FastTrack™
              </div>
              <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter text-white leading-[1]">When every day matters.</h2>
              <p className="text-white/85 mt-4 max-w-xl leading-relaxed">Urgent RFQs, express supplier search, priority inspections and expedited shipping — handled by a dedicated sourcing team.</p>
            </div>
            <Link to="/fasttrack" data-testid="home-fasttrack-btn" className="relative shrink-0 inline-flex items-center gap-2 bg-white text-red-600 px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-navy hover:text-white transition-colors">
              Explore FastTrack <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ABOUT INTRO */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <Reveal><p className="overline mb-6">About SourceX Global</p></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.05]">
                Your procurement team, embedded in India.
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:pt-4">
            <Reveal delay={0.15}>
              <p className="text-lg text-muted-foreground leading-relaxed">
                SourceX Global helps international buyers source engineering products from reliable
                Indian manufacturers. We manage the entire journey — supplier identification, vendor
                development, RFQ management, cost optimization, quality inspection, factory audits,
                sampling, production monitoring, export documentation and global logistics — so your
                team gets dependable supply without the risk of managing it remotely.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border mt-12 border border-border">
              {WHY.slice(0, 4).map((w) => (
                <Reveal key={w.label} className="bg-card p-6">
                  <div className="font-display font-black text-3xl md:text-4xl text-brand-orange">
                    <Counter value={w.value} suffix={w.suffix} />
                  </div>
                  <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground mt-2">{w.label}</div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMMODITIES PREVIEW */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <Reveal><p className="overline mb-4">Engineering Commodities</p></Reveal>
              <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight">45+ commodities sourced.</h2></Reveal>
            </div>
            <Reveal delay={0.15}>
              <Link to="/commodities" className="font-mono uppercase text-[11px] tracking-[0.18em] text-brand-orange inline-flex items-center gap-2 hover:gap-3 transition-all">
                View All Commodities <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-border border border-border">
            {COMMODITIES.slice(0, 8).map((c, i) => (
              <Reveal key={c.title} delay={(i % 4) * 0.05} className="bg-card group p-6 hover:bg-navy transition-colors">
                <span className="h-10 w-10 bg-brand-orange grid place-items-center mb-5"><c.icon size={18} className="text-white" /></span>
                <h3 className="font-display font-bold text-sm group-hover:text-white transition-colors">{c.title}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="overline mb-4">Industries Served</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-14">Built for every sector.</h2></Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border border border-border">
          {INDUSTRIES.map((ind, i) => (
            <Reveal key={ind.title} delay={(i % 6) * 0.04} className="bg-card p-6 flex flex-col items-center text-center gap-3 group hover:bg-brand-orange transition-colors">
              <ind.icon size={28} className="text-brand-orange group-hover:text-white transition-colors" strokeWidth={1.5} />
              <span className="font-mono uppercase text-[10px] tracking-[0.12em] text-muted-foreground group-hover:text-white transition-colors">{ind.title}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHY / COUNTERS */}
      <section className="bg-navy text-white py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal><p className="overline mb-4">Why Choose SourceX Global</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-16">Results you can measure.</h2></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {WHY.map((w, i) => (
              <Reveal key={w.label} delay={(i % 3) * 0.06} className="bg-navy p-8">
                <div className="font-display font-black text-4xl md:text-5xl text-brand-orange"><Counter value={w.value} suffix={w.suffix} /></div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-white/60 mt-3">{w.label}</div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-3">
            {WHY_POINTS.map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-white/70 text-sm">
                <CheckCircle2 size={15} className="text-brand-orange" /> {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="overline mb-4">Our Process</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-16 max-w-2xl">Ten steps, one accountable partner.</h2></Reveal>
        <div className="relative border-l-2 border-border ml-3">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={(i % 4) * 0.05} className="relative pl-10 pb-10 last:pb-0">
              <span className="absolute -left-[11px] top-0 h-5 w-5 bg-brand-orange grid place-items-center">
                <span className="h-2 w-2 bg-white" />
              </span>
              <div className="font-mono text-brand-orange text-sm mb-1">{p.n}</div>
              <h3 className="font-display font-bold text-xl mb-1">{p.title}</h3>
              <p className="text-muted-foreground leading-relaxed max-w-xl">{p.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal><p className="overline mb-4">Testimonials</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-16">Trusted worldwide.</h2></Reveal>
          <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={(i % 2) * 0.08} className="bg-card p-8 md:p-10">
                <Quote size={28} className="text-brand-orange mb-5" />
                <p className="text-foreground/85 leading-relaxed mb-6 text-lg">"{t.quote}"</p>
                <div className="font-display font-bold">{t.name}</div>
                <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-muted-foreground mt-1">{t.role}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1000px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="overline mb-4">FAQ</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-14">Questions, answered.</h2></Reveal>
        <div className="border border-border divide-y divide-border">
          {FAQ.map((f, i) => (
            <div key={f.q} className="bg-card">
              <button
                data-testid={`faq-${i}`}
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 text-left p-6 hover:text-brand-orange transition-colors"
              >
                <span className="font-display font-bold text-lg">{f.q}</span>
                {openFaq === i ? <Minus size={20} className="text-brand-orange shrink-0" /> : <Plus size={20} className="shrink-0" />}
              </button>
              {openFaq === i && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-6 pb-6 -mt-2">
                  <p className="text-muted-foreground leading-relaxed">{f.a}</p>
                </motion.div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-brand-orange text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.08] pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32 text-center">
          <Reveal><h2 className="font-display font-black text-4xl md:text-7xl tracking-tighter mb-8 max-w-4xl mx-auto">Ready to source smarter?</h2></Reveal>
          <Reveal delay={0.1}><p className="text-white/90 text-lg max-w-xl mx-auto mb-10">Send us your requirement and get a competitive, transparent quote from audited Indian suppliers.</p></Reveal>
          <Reveal delay={0.15}>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/quote" className="bg-navy text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-navy-soft transition-colors">Request a Quote</Link>
              <Link to="/contact" className="border border-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-white hover:text-brand-orange transition-colors">Contact Us</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
